import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ZoomInIcon, XIcon } from 'lucide-react';
import { useBackNavigation } from '../hooks/useBackNavigation';
export function BioMaxing() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const goBack = useBackNavigation('/ux-ui-design');
  return <div className="w-full min-h-screen bg-white" data-id="element-499">
      {/* Image Modal */}
      {selectedImage && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setSelectedImage(null)} data-id="element-500">
          <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white hover:text-cyan-400 transition-colors" data-id="element-501">
            <XIcon className="w-8 h-8" data-id="element-502" />
          </button>
          <motion.img initial={{
        scale: 0.9
      }} animate={{
        scale: 1
      }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain" data-id="element-503" />
        </motion.div>}
      {/* Header */}
      <header className="py-8 px-6 bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-504">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-505">
          <button onClick={goBack} className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-id="element-506">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-507" />
            <span className="font-light" data-id="element-508">Back</span>
          </button>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-509">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-cyan-50/30 to-stone-50" data-id="element-510">
        <div className="max-w-5xl mx-auto" data-id="element-511">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-512">
            <p className="text-cyan-600 font-light tracking-wide uppercase mb-4" data-id="element-513">
              Creative Direction, UX/UI Design
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-514">
              BioMaxing
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-515">
              Biohacking multi-platform application spanning Web, Mobile and
              Telegram
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-516">
              <p className="text-lg" data-id="element-517">
                <span className="font-normal text-stone-900" data-id="element-518">Role:</span>{' '}
                Co-Founder, Creative Director, Designer, Marketing
                Director
              </p>
              <p className="text-lg" data-id="element-519">
                <span className="font-normal text-stone-900" data-id="element-520">Platform:</span>{' '}
                Web, Mobile (iOS/Android), Telegram Bot
              </p>
              <p className="text-lg" data-id="element-521">
                <span className="font-normal text-stone-900" data-id="element-522">Status:</span> In
                Development
              </p>
              <p className="text-lg" data-id="element-523">
                <span className="font-normal text-stone-900" data-id="element-524">Year:</span> 2025
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Overview */}
      <section className="py-20 px-6 bg-white" data-id="element-525">
        <div className="max-w-5xl mx-auto" data-id="element-526">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-527">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-528">
              Project Overview
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-529">
              BioMaxing is a startup we are currently actively developing with
              my technical co-founder. The project aims to create a
              comprehensive biohacking ecosystem that makes health optimization
              accessible and actionable for users at all levels of experience.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-530">
              As Creative Director and UX/UI Designer, I am responsible for the
              entire visual identity, user experience strategy, and interface
              design across all platforms. My role also encompasses marketing
              direction, ensuring the brand resonates with our target audience
              of health-conscious individuals and biohacking enthusiasts.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed" data-id="element-531">
              The platform combines scientific research, community expertise,
              and advanced tracking tools to create personalized biohacking
              journeys. Users can access a comprehensive knowledge library,
              track their health metrics, connect with experts, and discover
              evidence-based practices for optimizing their well-being.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Website Design - Desktop & Mobile */}
      <section className="py-20 px-6 bg-white" data-id="element-532">
        <div className="max-w-7xl mx-auto" data-id="element-533">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-534">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-535">
              Final Website Design
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-536">
              The refined website design balances futuristic aesthetics with
              accessibility, creating a seamless experience across all devices.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-start" data-id="element-537">
              {/* Desktop - Takes 3 columns */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="md:col-span-3 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/homepage%204%20(1).png')} data-id="element-538">
                <div className="relative overflow-hidden rounded-2xl shadow-xl bg-stone-900" data-id="element-539">
                  <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/homepage%204%20(1).png" alt="BioMaxing Desktop Website" className="w-full h-auto object-contain" data-id="element-540" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-541">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-542" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-543">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-544">
                    Desktop Experience
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-545">
                    Full website layout with hero section, feature highlights,
                    and clear navigation
                  </p>
                </div>
              </motion.div>
              {/* Mobile - Takes 1 column */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="md:col-span-1 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/iPhone%20homepage.png')} data-id="element-546">
                <div className="relative overflow-hidden rounded-2xl shadow-xl bg-stone-900" data-id="element-547">
                  <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/iPhone%20homepage.png" alt="BioMaxing Mobile Website" className="w-full h-auto object-contain" data-id="element-548" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-549">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-550" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-551">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-552">
                    Mobile Experience
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-553">
                    Optimized responsive layout for on-the-go access
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Knowledge Library Planning */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-554">
        <div className="max-w-7xl mx-auto" data-id="element-555">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-556">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-557">
              Knowledge Library Architecture
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-558">
              A comprehensive content architecture mapping the entire biohacking
              knowledge base—from foundational principles of human physiology to
              advanced optimization stacks and practices. This planning document
              guides the development of our educational content strategy.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/good.png')} data-id="element-559">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-560">
                <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/good.png" alt="BioMaxing Knowledge Library Planning" className="w-full h-auto" data-id="element-561" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-562">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-563" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-564">
                Content structure planning for the comprehensive wellness and
                biohacking library
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Mobile App Design Development */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-565">
        <div className="max-w-7xl mx-auto" data-id="element-566">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-567">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-568">
              Mobile App Design Process
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-569">
              The mobile application design process involves extensive iteration
              on user flows, interface components, and visualization patterns.
              This comprehensive design system ensures consistency across all
              mobile touchpoints while maintaining flexibility for future
              feature development.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/app%20wireframe.png')} data-id="element-570">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-571">
                <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/app%20wireframe.png" alt="BioMaxing Mobile App Design Development" className="w-full h-auto" data-id="element-572" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-573">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-574" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-575">
                Comprehensive mobile app design system showing various screens
                and user flows
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Initial Website Concept */}
      <section className="py-20 px-6 bg-white" data-id="element-576">
        <div className="max-w-7xl mx-auto" data-id="element-577">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-578">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-579">
              Initial Website Concept
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-580">
              The website design evolved through multiple iterations, refining
              the visual language and user experience to create the polished
              interface we have today.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-581">
              {/* First Concept */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Homepage_new.png')} data-id="element-582">
                <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900 h-[600px]" data-id="element-583">
                  <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Homepage_new.png" alt="BioMaxing Early Website Concept" className="w-full h-full object-cover object-top" data-id="element-584" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-585">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-586" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-587">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-588">
                    Early Concept 1
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-589">
                    Initial exploration establishing the futuristic aesthetic
                  </p>
                </div>
              </motion.div>
              {/* Second Concept */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/home1.1.png')} data-id="element-590">
                <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900 h-[600px]" data-id="element-591">
                  <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/home1.1.png" alt="BioMaxing Refined Website Concept" className="w-full h-full object-cover object-top" data-id="element-592" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-593">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-594" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-595">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-596">
                    Early Concept 2
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-597">
                    Initial exploration focusing on familiar page structures
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Logo Design Process */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-598">
        <div className="max-w-7xl mx-auto" data-id="element-599">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-600">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-601">
              Logo Design & Brand Identity
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-602">
              The logo system evolved through multiple iterations to create a
              flexible identity that works across all platforms while
              maintaining instant brand recognition.
            </p>
            {/* Design Iterations Grid */}
            <div className="mb-16" data-id="element-603">
              <h3 className="text-xl font-light text-stone-900 mb-8" data-id="element-604">
                Design Iterations
              </h3>
              {/* First Row - 3 Images */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-605">
                {/* Initial Concepts */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image%2081.png')} data-id="element-606">
                  <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-100" data-id="element-607">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image%2081.png" alt="Initial logo concepts" className="w-full h-auto" data-id="element-608" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-609">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-610" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-611">
                    Initial concept explorations
                  </p>
                </motion.div>
                {/* Triangle Concepts */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/triangle.png')} data-id="element-612">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-613">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/triangle.png" alt="Triangle-based concepts" className="w-full h-auto" data-id="element-614" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-615">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-616" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-617">
                    Triangle-based explorations
                  </p>
                </motion.div>
                {/* Angled Concepts */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.2
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/angled.png')} data-id="element-618">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-619">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/angled.png" alt="Angled design variations" className="w-full h-auto" data-id="element-620" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-621">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-622" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-623">
                    Angled design variations
                  </p>
                </motion.div>
              </div>
              {/* Second Row - 1 Image */}
              <div className="mb-6" data-id="element-624">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.3
              }} className="group cursor-pointer max-w-md mx-auto" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/minimalist.png')} data-id="element-625">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-626">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/minimalist.png" alt="Minimalist approach" className="w-full h-auto" data-id="element-627" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-628">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-629" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-630">
                    Minimalist approach
                  </p>
                </motion.div>
              </div>
              {/* Third Row - 3 Images */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-631">
                {/* Hand Symbol Development */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.4
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image_2025-11-06_21-46-47.png')} data-id="element-632">
                  <div className="relative overflow-hidden rounded-xl shadow-lg h-64" data-id="element-633">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image_2025-11-06_21-46-47.png" alt="Hand symbol development" className="w-full h-full object-cover object-top" data-id="element-634" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-635">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-636" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-637">
                    Hand symbol development
                  </p>
                </motion.div>
                {/* Graphic Exploration */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.5
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image_2025-11-06_21-47-12.png')} data-id="element-638">
                  <div className="relative overflow-hidden rounded-xl shadow-lg h-64" data-id="element-639">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image_2025-11-06_21-47-12.png" alt="Graphic exploration" className="w-full h-full object-cover object-top" data-id="element-640" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-641">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-642" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-643">
                    Graphic exploration
                  </p>
                </motion.div>
                {/* Refined Hand Concept */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/wristless.png')} data-id="element-644">
                  <div className="relative overflow-hidden rounded-xl shadow-lg h-64" data-id="element-645">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/wristless.png" alt="Refined hand concept" className="w-full h-full object-cover object-top" data-id="element-646" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-647">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-648" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-649">
                    Refined hand concept
                  </p>
                </motion.div>
              </div>
            </div>
            {/* Final Selected System */}
            <div data-id="element-650">
              <h3 className="text-xl font-light text-stone-900 mb-6" data-id="element-651">
                Final Logo System
              </h3>
              <p className="text-stone-600 font-light mb-8 max-w-3xl" data-id="element-652">
                The hand symbol emerged as the core visual anchor, with a
                modular system allowing context-specific variations for
                different platforms while maintaining instant brand recognition.
              </p>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/final_3.png')} data-id="element-653">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-654">
                  <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/final_3.png" alt="BioMaxing Final Logo System" className="w-full h-auto" data-id="element-655" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-656">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-657" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-658">
                  Multi-channel logo system with variations for different
                  touchpoints
                </p>
              </motion.div>
            </div>
            {/* Social Media Previews */}
            <div className="mt-16" data-id="element-659">
              <h3 className="text-xl font-light text-stone-900 mb-6" data-id="element-660">
                Social Media Applications
              </h3>
              <p className="text-stone-600 font-light mb-8 max-w-3xl" data-id="element-661">
                The brand identity extends to social media platforms with
                attractive previews and tailored visuals that maintain
                consistency while adapting to different formats and messaging
                needs.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-662">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="md:col-span-3 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Ecosystem%20of%20your%20well-being.png')} data-id="element-663">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-664">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Ecosystem%20of%20your%20well-being.png" alt="Social media preview - Ecosystem messaging" className="w-full h-auto" data-id="element-665" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-666">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-667" />
                    </div>
                  </div>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="md:col-span-1 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Your%20paragraph%20text%20(4).png')} data-id="element-668">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-669">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Your%20paragraph%20text%20(4).png" alt="Social media preview - Brand messaging" className="w-full h-auto" data-id="element-670" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-671">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-672" />
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Design Approach */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-673">
        <div className="max-w-5xl mx-auto" data-id="element-674">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-675">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-676">
              Design Approach
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-677">
              <p data-id="element-678">
                The design strategy for BioMaxing centers on making complex
                scientific concepts accessible without sacrificing depth. Every
                interface decision is informed by the goal of creating a
                seamless and consistent brand experience across web, mobile, and
                messaging platforms.
              </p>
              <p data-id="element-679">
                The visual language combines futuristic aesthetics with
                approachable, human-centered design. The color palette -
                anchored by cyan and teal tones - evokes both technology and
                wellness, while the typography and spacing create a sense of
                clarity and breathing room essential for processing health
                information.
              </p>
              <p data-id="element-680">
                As the project continues to develop, the design system evolves
                based on user feedback and technical constraints, always
                maintaining the core principle: empowering users to take control
                of their health through informed, personalized optimization
                strategies.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}